import { MarkedOptions, MarkedRenderer } from 'ngx-markdown';
import { Parser } from 'marked';

const ALLOWED_LINK_PROTOCOLS = new Set(['http:', 'https:', 'mailto:', 'tel:']);
const BLOCKED_URL_SCHEMES = /^(?:javascript|data|vbscript|file):/i;

function escapeHtml(input: string): string {
  return input
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');
}

function hasExplicitScheme(value: string): boolean {
  return /^[a-zA-Z][a-zA-Z\d+\-.]*:/.test(value);
}

function parseAbsoluteUrl(value: string): URL | null {
  try {
    return new URL(value);
  } catch {
    return null;
  }
}

export function isSafeAdaLinkUrl(value: string): boolean {
  const href = value.trim();
  if (!href || BLOCKED_URL_SCHEMES.test(href)) {
    return false;
  }

  if (hasExplicitScheme(href)) {
    const parsed = parseAbsoluteUrl(href);
    return !!parsed && ALLOWED_LINK_PROTOCOLS.has(parsed.protocol);
  }

  return (
    href.startsWith('/') ||
    href.startsWith('./') ||
    href.startsWith('../') ||
    href.startsWith('#') ||
    href.startsWith('?') ||
    href.startsWith('//')
  );
}

export function isSafeAdaImageUrl(value: string): boolean {
  const src = value.trim();
  const parsed = parseAbsoluteUrl(src);
  return !!parsed && (parsed.protocol === 'http:' || parsed.protocol === 'https:');
}

/** Develop parity: GFM markdown with safe link/image renderers for Ada chat bubbles. */
export function adaMarkedOptionsFactory(): MarkedOptions {
  const renderer = new MarkedRenderer();

  renderer.link = ({ href, title, tokens, text }) => {
    const linkHref = (href ?? '').trim();
    const linkText = tokens?.length ? Parser.parseInline(tokens) : escapeHtml((text ?? linkHref).trim());

    if (!isSafeAdaLinkUrl(linkHref)) {
      return escapeHtml((text ?? linkHref).trim());
    }

    const safeHref = escapeHtml(linkHref);
    const titleAttribute = title ? ` title="${escapeHtml(title)}"` : '';

    return `<a href="${safeHref}" target="_blank" rel="noopener noreferrer"${titleAttribute}>${linkText}</a>`;
  };

  renderer.image = ({ href, title, text }) => {
    const imageSrc = (href ?? '').trim();
    const imageAlt = (text ?? '').trim();

    if (!isSafeAdaImageUrl(imageSrc)) {
      return escapeHtml(imageAlt);
    }

    const safeSrc = escapeHtml(imageSrc);
    const safeAlt = escapeHtml(imageAlt);
    const titleAttribute = title ? ` title="${escapeHtml(title)}"` : '';

    return `<img src="${safeSrc}" alt="${safeAlt}" loading="lazy" decoding="async"${titleAttribute}>`;
  };

  return {
    gfm: true,
    breaks: true,
    pedantic: false,
    renderer,
  };
}
